import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import '../styles/terms-conditions.css';

const sections = [
  { id: 'agreement-to-terms', title: '1. Agreement to Terms' },
  { id: 'services', title: '2. Services' },
  { id: 'use-of-website', title: '3. Use of Website' },
  { id: 'service-agreements', title: '4. Service Agreements' },
  { id: 'sms-communications', title: '5. SMS/Text Message Communications' },
  { id: 'estimates-pricing', title: '6. Estimates and Pricing' },
  { id: 'payment-terms', title: '7. Payment Terms' },
  { id: 'limitation-of-liability', title: '8. Limitation of Liability' },
  { id: 'indemnification', title: '9. Indemnification' },
  { id: 'disclaimer', title: '10. Disclaimer of Warranties' },
  { id: 'governing-law', title: '11. Governing Law' },
  { id: 'severability', title: '12. Severability' },
  { id: 'changes', title: '13. Changes to These Terms' },
  { id: 'contact-us', title: '14. Contact Us' },
];

const TermsConditions = () => {
  const [activeSection, setActiveSection] = useState(null);

  const handleNavClick = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  return (
    <div className="terms-page">
      <Navbar />

      {/* Hero Section */}
      <section className="terms-hero">
        <div className="terms-hero-overlay">
          <div className="terms-hero-content">
            <p className="terms-hero-eyebrow">Legal &amp; Compliance</p>
            <h1 className="terms-hero-title">Terms &amp; Conditions</h1>
            <p className="terms-hero-subtitle">
              All Seasons Landscaping, Inc.
            </p>
            <div className="terms-effective-date">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Effective Date: April 2, 2026
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="terms-layout">

        {/* Sidebar TOC */}
        <aside className="terms-sidebar">
          <div className="sidebar-sticky">
            <h4 className="sidebar-title">Contents</h4>
            <nav className="sidebar-nav">
              {sections.map((s) => (
                <button
                  key={s.id}
                  className={`sidebar-link ${activeSection === s.id ? 'active' : ''}`}
                  onClick={() => handleNavClick(s.id)}
                >
                  {s.title}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Terms Body */}
        <main className="terms-body">

          {/* Section 1 */}
          <section id="agreement-to-terms" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">01</span>
              Agreement to Terms
            </h2>
            <p>
              These Terms and Conditions ("Terms") govern your access to and use of the website operated by All Seasons Landscaping, Inc. ("Company," "we," "us," or "our") at <a href="https://www.allseasons.info" className="policy-link" target="_blank" rel="noopener noreferrer">www.allseasons.info</a>, as well as any related services, communications, and interactions with our company. By accessing our website or engaging our services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our website or services.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 2 */}
          <section id="services" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">02</span>
              Services
            </h2>
            <p>
              All Seasons Landscaping, Inc. provides professional landscaping, grounds maintenance, landscape construction, irrigation, design, and snow removal services in the Wood River Valley, Idaho area. The specific scope, pricing, and terms of any service engagement will be outlined in a separate service agreement, estimate, or proposal provided to you.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 3 */}
          <section id="use-of-website" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">03</span>
              Use of Website
            </h2>

            <h3 className="policy-subsection-title">3.1 Permitted Use</h3>
            <p>
              You may use our website for lawful purposes related to learning about our services, requesting estimates, or contacting us. You agree not to use the website in any way that could damage, disable, or impair the website, or interfere with any other party's use of the website.
            </p>

            <h3 className="policy-subsection-title">3.2 Intellectual Property</h3>
            <p>
              All content on this website, including text, images, graphics, logos, photographs, and design elements, is the property of All Seasons Landscaping, Inc. or its content suppliers and is protected by United States copyright and trademark laws. You may not reproduce, distribute, modify, or create derivative works from any content on this website without our prior written consent.
            </p>

            <h3 className="policy-subsection-title">3.3 Accuracy of Information</h3>
            <p>
              We strive to ensure that the information on our website is accurate and up to date. However, we make no warranties or representations regarding the completeness, accuracy, or reliability of any information on the website. Service availability, pricing, and scope may vary and are subject to confirmation through direct communication with our team.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 4 */}
          <section id="service-agreements" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">04</span>
              Service Agreements
            </h2>
            <p>
              Landscaping and related services provided by the Company are governed by individual service agreements, estimates, or proposals executed between the Company and the client. These Terms supplement but do not replace the terms of any individual service agreement. In the event of a conflict between these Terms and a service agreement, the service agreement will control.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 5 */}
          <section id="sms-communications" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">05</span>
              SMS/Text Message Communications
            </h2>

            <h3 className="policy-subsection-title">5.1 Opt-In</h3>
            <p>
              By providing your mobile phone number and consenting to receive text messages, you agree to receive service-related SMS/MMS messages from All Seasons Landscaping. These messages may include crew scheduling notifications, appointment reminders, service updates, and weather-related communications.
            </p>

            <h3 className="policy-subsection-title">5.2 Message Frequency and Rates</h3>
            <p>
              Message frequency varies depending on your service schedule and relevant updates. Standard message and data rates may apply as determined by your mobile carrier. All Seasons Landscaping is not responsible for any charges incurred from your carrier for receiving text messages.
            </p>

            <h3 className="policy-subsection-title">5.3 Opt-Out and Help</h3>
            <p>
              You may opt out of text messages at any time by replying <strong>STOP</strong>. For assistance, reply <strong>HELP</strong> or contact us at <a href="mailto:matt@allseasons.info" className="policy-link">matt@allseasons.info</a> or <a href="tel:2088068440" className="policy-link">(208) 806-8440</a>. Opting out of text messages will not affect your service agreement or the quality of services provided.
            </p>

            <h3 className="policy-subsection-title">5.4 SMS Data Privacy</h3>
            <div className="policy-highlight-box">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p>
                Your phone number and SMS consent information will not be sold, rented, or shared with third parties or affiliates for marketing purposes. Please refer to our <a href="/privacy-policy" className="policy-link">Privacy Policy</a> for complete details on how we handle your information.
              </p>
            </div>
          </section>

          <div className="section-divider" />

          {/* Section 6 */}
          <section id="estimates-pricing" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">06</span>
              Estimates and Pricing
            </h2>
            <p>
              Estimates provided by the Company are based on the information available at the time of the estimate and are subject to change based on actual site conditions, material costs, and scope modifications. An estimate does not constitute a binding contract unless accepted in writing by both parties. Final pricing will be confirmed in a signed service agreement.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 7 */}
          <section id="payment-terms" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">07</span>
              Payment Terms
            </h2>
            <p>
              Payment terms are specified in individual service agreements. Unless otherwise stated, invoices are due upon receipt. The Company reserves the right to suspend or terminate services for accounts with outstanding balances. Late payments may be subject to interest charges as permitted by applicable law.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 8 */}
          <section id="limitation-of-liability" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">08</span>
              Limitation of Liability
            </h2>
            <p>
              To the fullest extent permitted by law, All Seasons Landscaping, Inc. shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of our website or services. Our total liability for any claim arising under these Terms shall not exceed the amount you paid to the Company for the specific service giving rise to the claim.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 9 */}
          <section id="indemnification" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">09</span>
              Indemnification
            </h2>
            <p>
              You agree to indemnify, defend, and hold harmless All Seasons Landscaping, Inc., its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including reasonable attorneys' fees) arising out of or related to your breach of these Terms, your use of our website, or your violation of any applicable law or regulation.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 10 */}
          <section id="disclaimer" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">10</span>
              Disclaimer of Warranties
            </h2>
            <p>
              Our website and services are provided on an "as is" and "as available" basis. The Company makes no warranties, express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that our website will be uninterrupted, secure, or error-free.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 11 */}
          <section id="governing-law" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">11</span>
              Governing Law
            </h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the State of Idaho, without regard to its conflict of law principles. Any disputes arising under these Terms shall be resolved in the state or federal courts located in Blaine County, Idaho.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 12 */}
          <section id="severability" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">12</span>
              Severability
            </h2>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary, and the remaining provisions will continue in full force and effect.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 13 */}
          <section id="changes" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">13</span>
              Changes to These Terms
            </h2>
            <p>
              We reserve the right to modify these Terms at any time. Changes will be effective upon posting to our website with an updated effective date. Your continued use of our website or services after any changes constitutes your acceptance of the revised Terms.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 14 */}
          <section id="contact-us" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">14</span>
              Contact Us
            </h2>
            <p>If you have any questions about these Terms and Conditions, please contact us:</p>

            <div className="contact-info-grid">
              <div className="contact-info-item">
                <span className="contact-info-label">Company</span>
                <span className="contact-info-value">All Seasons Landscaping, Inc.</span>
              </div>
              <div className="contact-info-item">
                <span className="contact-info-label">Location</span>
                <span className="contact-info-value">Sun Valley, Idaho</span>
              </div>
              <div className="contact-info-item">
                <span className="contact-info-label">Email</span>
                <a href="mailto:matt@allseasons.info" className="contact-info-value policy-link">matt@allseasons.info</a>
              </div>
              <div className="contact-info-item">
                <span className="contact-info-label">Phone</span>
                <a href="tel:2088068440" className="contact-info-value policy-link">(208) 806-8440</a>
              </div>
            </div>
          </section>

        </main>
      </div>
    </div>
  );
};

export default TermsConditions;
